import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { pqrMaxAttachments } from "@/features/pqr/pqr.types";

type FileUploadChipsProps = {
  files: File[];
  onRemove: (index: number) => void;
};

function formatBytes(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadChips({ files, onRemove }: FileUploadChipsProps) {
  const { t } = useTranslation();

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/50">
        {t("pqr.fileUpload.count", { count: files.length, max: pqrMaxAttachments })}
      </p>
      <div className="space-y-3">
        {files.map((file, index) => (
          <div
            key={`${file.name}-${file.size}-${index}`}
            className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e4d8ca] pt-3 text-sm text-ink"
          >
            <div className="min-w-0">
              <p className="truncate font-medium">{file.name}</p>
              <p className="mt-1 text-xs text-ink/55">{formatBytes(file.size)}</p>
            </div>
            <Button
              aria-label={t("pqr.actions.removeAttachment", { fileName: file.name })}
              className="px-3 py-1 text-[11px]"
              onClick={() => onRemove(index)}
              size="sm"
              variant="ghost"
            >
              {t("common.remove")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
